/**
 * 剧本角色管理页面
 * 提供剧本角色的增删改查功能，支持按剧本筛选和管理角色简介
 */
import React, { useState, useEffect, useCallback } from 'react'
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Modal,
  Form,
  Select,
  message,
  Popconfirm,
  Row,
  Col,
  Typography,
  Tooltip,
  Empty,
  Descriptions,
  Divider,
  Avatar,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  TeamOutlined,
  UserOutlined,
  BookOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { characterApi, scriptApi } from '@/services/api'
import ImageUpload from '@/components/ImageUpload'

const { Search } = Input
const { Option } = Select
const { Text, Title } = Typography
const { TextArea } = Input

// 角色类型映射
const CHARACTER_TYPE_LABELS: Record<string, string> = {
  main: '主角',
  supporting: '配角',
  npc: 'NPC',
  villain: '反派',
  hero: '英雄',
  neutral: '中立',
}

const SCRIPT_STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  published: '已发布',
}

// 角色接口
interface Character {
  id: number
  uuid: string
  script_id: number
  character_name: string
  character_type?: string
  played_by?: string
  description?: string
  avatar_url?: string
  personality_traits?: string[]
  backstory?: string
  relationships?: Record<string, unknown>
  display_order: number
  intro?: string
  created_at: string
  updated_at: string
}

// 剧本接口
interface Script {
  id: number
  title: string
  status: string
  genre?: string
}

// 角色详情抽屉组件
const CharacterDetailDrawer: React.FC<{
  visible: boolean
  character: Character | null
  onClose: () => void
}> = ({ visible, character, onClose }) => {
  if (!character) return null

  return (
    <Modal
      title={
        <Space>
          <UserOutlined />
          <span>角色详情 - {character.character_name}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      <Descriptions column={2} bordered size="small" style={{ marginTop: 16 }}>
        <Descriptions.Item label="角色名称" span={2}>
          <Text strong>{character.character_name}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="角色类型">
          <Tag color={character.character_type === 'main' ? 'blue' : 'default'}>
            {CHARACTER_TYPE_LABELS[character.character_type || ''] || character.character_type || '未设置'}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="排序权重">
          {character.display_order}
        </Descriptions.Item>
        <Descriptions.Item label="创建时间" span={2}>
          {new Date(character.created_at).toLocaleString('zh-CN')}
        </Descriptions.Item>
        <Descriptions.Item label="更新时间" span={2}>
          {new Date(character.updated_at).toLocaleString('zh-CN')}
        </Descriptions.Item>
      </Descriptions>

      <Divider>角色简介</Divider>
      <TextArea
        value={character.intro || '暂无简介'}
        rows={6}
        readOnly
        style={{ fontFamily: 'monospace' }}
      />

      {character.description && (
        <>
          <Divider>详细描述</Divider>
          <TextArea
            value={character.description}
            rows={4}
            readOnly
            style={{ fontFamily: 'monospace' }}
          />
        </>
      )}

      {character.backstory && (
        <>
          <Divider>背景故事</Divider>
          <TextArea
            value={character.backstory}
            rows={4}
            readOnly
            style={{ fontFamily: 'monospace' }}
          />
        </>
      )}

      {character.personality_traits && character.personality_traits.length > 0 && (
        <>
          <Divider>性格特征</Divider>
          <Space wrap>
            {character.personality_traits.map((trait, index) => (
              <Tag key={index} color="green">{trait}</Tag>
            ))}
          </Space>
        </>
      )}

      {character.relationships && Object.keys(character.relationships).length > 0 && (
        <>
          <Divider>角色关系</Divider>
          <TextArea
            value={JSON.stringify(character.relationships, null, 2)}
            rows={6}
            readOnly
            style={{ fontFamily: 'monospace' }}
          />
        </>
      )}
    </Modal>
  )
}

// 新建/编辑角色弹窗组件
const CharacterFormModal: React.FC<{
  visible: boolean
  onCancel: () => void
  onOk: (values: any) => Promise<void>
  editingCharacter: Character | null
  scriptId: number
}> = ({ visible, onCancel, onOk, editingCharacter, scriptId }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (visible) {
      if (editingCharacter) {
        form.setFieldsValue({
          character_name: editingCharacter.character_name,
          character_type: editingCharacter.character_type,
          played_by: editingCharacter.played_by,
          description: editingCharacter.description,
          avatar_url: editingCharacter.avatar_url,
          intro: editingCharacter.intro,
          backstory: editingCharacter.backstory,
          personality_traits: editingCharacter.personality_traits?.join(', '),
          display_order: editingCharacter.display_order,
          // JSON 编辑器：编辑时把对象序列化为字符串展示给用户
          // HACK: 检查 typeof object 避免 JSON.stringify(null) 渲染成 "null" 字符串
          relationships:
            editingCharacter.relationships &&
            typeof editingCharacter.relationships === 'object'
              ? JSON.stringify(editingCharacter.relationships, null, 2)
              : '',
        })
      } else {
        form.resetFields()
        form.setFieldsValue({
          display_order: 0,
          character_type: 'supporting',
          played_by: 'player',
          relationships: '',
        })
      }
    }
  }, [visible, editingCharacter, form])

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      // 处理性格特征（从逗号分隔的字符串转为数组）
      const personalityTraits = values.personality_traits
        ? values.personality_traits.split(',').map((s: string) => s.trim()).filter(Boolean)
        : []

      // 处理 relationships JSON 字段：解析 + 校验
      let relationships: Record<string, unknown> | null = null
      const rawRelationships = (values.relationships || '').trim()
      if (rawRelationships) {
        try {
          const parsed = JSON.parse(rawRelationships)
          if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
            message.error('角色关系必须是 JSON 对象，例如 {"npc_guard": "ally"}')
            setLoading(false)
            return
          }
          relationships = parsed as Record<string, unknown>
        } catch (err) {
          message.error(`角色关系 JSON 格式错误：${(err as Error).message}`)
          setLoading(false)
          return
        }
      }

      const characterData = {
        script_id: scriptId,
        character_name: values.character_name,
        character_type: values.character_type || 'supporting',
        played_by: values.played_by || 'player',
        description: values.description || '',
        avatar_url: values.avatar_url || '',
        intro: values.intro || '',
        backstory: values.backstory || '',
        personality_traits: personalityTraits,
        display_order: values.display_order || 0,
        relationships,
      }

      await onOk(characterData)
      form.resetFields()
    } catch (error) {
      // 表单验证失败
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={
        <Space>
          <UserOutlined />
          <span>{editingCharacter ? '编辑角色' : '新建角色'}</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={600}
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          label="角色名称"
          name="character_name"
          rules={[{ required: true, message: '请输入角色名称' }]}
        >
          <Input placeholder="输入角色名称" maxLength={100} showCount />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="角色类型"
              name="character_type"
            >
              <Select placeholder="选择角色类型">
                <Option value="main">主角</Option>
                <Option value="supporting">配角</Option>
                <Option value="npc">NPC</Option>
                <Option value="villain">反派</Option>
                <Option value="hero">英雄</Option>
                <Option value="neutral">中立</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="扮演者"
              name="played_by"
              extra="player=玩家 / actor=演员 / 不区分"
            >
              <Select placeholder="选择扮演者类型" allowClear>
                <Option value="player">玩家</Option>
                <Option value="actor">演员</Option>
                <Option value="">不区分</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="排序权重"
              name="display_order"
            >
              <Input type="number" min={0} placeholder="数字越小越靠前" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="头像"
          name="avatar_url"
          extra="上传并预览角色头像"
        >
          <ImageUpload />
        </Form.Item>

        <Form.Item
          label="角色简介"
          name="intro"
          extra="显示给玩家的角色介绍/剧情介绍"
        >
          <TextArea
            rows={4}
            placeholder="输入角色简介（显示给玩家）"
            maxLength={2000}
            showCount
          />
        </Form.Item>

        <Form.Item
          label="详细描述"
          name="description"
          extra="角色的详细描述信息"
        >
          <TextArea
            rows={3}
            placeholder="输入详细描述"
            maxLength={2000}
          />
        </Form.Item>

        <Form.Item
          label="背景故事"
          name="backstory"
        >
          <TextArea
            rows={3}
            placeholder="输入角色背景故事"
            maxLength={5000}
          />
        </Form.Item>

        <Form.Item
          label="性格特征"
          name="personality_traits"
          extra="多个特征用逗号分隔"
        >
          <Input placeholder="例如: 冷静,勇敢,机智" />
        </Form.Item>

        <Form.Item
          label="角色关系 (JSON)"
          name="relationships"
          extra={
            <span>
              JSON 对象格式，例如 <code>{'{"npc_guard": "ally", "merchant": "rival"}'}</code>
              ；留空表示无关系数据；最大 50,000 字符
            </span>
          }
          rules={[
            {
              validator: async (_: unknown, value: string) => {
                if (!value || !value.trim()) return
                // HACK: 前端层 DoS 防护，防止恶意用户粘贴 MB 级 JSON 卡浏览器
                if (value.length > 50000) {
                  throw new Error('角色关系 JSON 不能超过 50,000 字符')
                }
                try {
                  const parsed = JSON.parse(value)
                  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
                    throw new Error('必须是 JSON 对象（不能是数组或基本类型）')
                  }
                } catch (err) {
                  throw new Error((err as Error).message)
                }
              },
            },
          ]}
        >
          <TextArea
            rows={4}
            maxLength={50000}
            placeholder='{"npc_guard": "ally"}'
            style={{ fontFamily: 'monospace' }}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

// 主页面组件
const CharacterManagement: React.FC = () => {
  const [scripts, setScripts] = useState<Script[]>([])
  const [selectedScriptId, setSelectedScriptId] = useState<number | null>(null)
  const [characters, setCharacters] = useState<Character[]>([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [detailVisible, setDetailVisible] = useState(false)
  const [editingCharacter, setEditingCharacter] = useState<Character | null>(null)
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null)
  const [searchText, setSearchText] = useState('')
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  })

  // 获取剧本列表
  const fetchScripts = useCallback(async () => {
    try {
      const response = await scriptApi.getScripts({ page: 1, page_size: 500 })
      if (response.data?.items) {
        setScripts(response.data.items)
      }
    } catch (error) {
      console.error('获取剧本列表失败:', error)
    }
  }, [])

  // 获取角色列表
  const fetchCharacters = useCallback(async () => {
    if (!selectedScriptId) {
      setCharacters([])
      return
    }

    setLoading(true)
    try {
      const params: any = {
        page: pagination.current,
        limit: pagination.pageSize,
        script_id: selectedScriptId,
      }
      if (searchText) {
        params.search = searchText
      }

      const response = await characterApi.getCharacters(params)
      if (response.data) {
        setCharacters(response.data.items || [])
        setPagination(prev => ({ ...prev, total: response.data.total || 0 }))
      }
    } catch (error: any) {
      message.error(error?.message || '获取角色列表失败')
    } finally {
      setLoading(false)
    }
  }, [selectedScriptId, pagination.current, pagination.pageSize, searchText])

  useEffect(() => {
    fetchScripts()
  }, [fetchScripts])

  useEffect(() => {
    if (selectedScriptId) {
      fetchCharacters()
    }
  }, [selectedScriptId, fetchCharacters])

  // 处理剧本切换
  const handleScriptChange = (scriptId: number) => {
    setSelectedScriptId(scriptId)
    setPagination(prev => ({ ...prev, current: 1 }))
    setSearchText('')
  }

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchText(value)
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  // 处理创建/更新角色
  const handleSaveCharacter = async (values: any) => {
    try {
      if (editingCharacter) {
        await characterApi.updateCharacter(editingCharacter.id, values)
        message.success('角色更新成功')
      } else {
        await characterApi.createCharacter(values)
        message.success('角色创建成功')
      }
      setModalVisible(false)
      setEditingCharacter(null)
      fetchCharacters()
    } catch (error: any) {
      message.error(error?.message || '保存失败')
    }
  }

  // 处理删除角色
  const handleDeleteCharacter = async (id: number) => {
    try {
      await characterApi.deleteCharacter(id)
      message.success('角色删除成功')
      fetchCharacters()
    } catch (error: any) {
      message.error(error?.message || '删除失败')
    }
  }

  // 打开编辑弹窗
  const openEditModal = (character?: Character) => {
    setEditingCharacter(character || null)
    setModalVisible(true)
  }

  // 打开详情抽屉
  const openDetailDrawer = (character: Character) => {
    setSelectedCharacter(character)
    setDetailVisible(true)
  }

  const selectedScript = scripts.find(s => s.id === selectedScriptId)
  const selectedScriptStatus = selectedScript?.status

  // 表格列定义
  const columns: ColumnsType<Character> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: number) => <Text copyable>{id}</Text>,
    },
    {
      title: '排序',
      dataIndex: 'display_order',
      key: 'display_order',
      width: 60,
      render: (order: number) => <Text>{order}</Text>,
    },
    {
      title: '角色名称',
      dataIndex: 'character_name',
      key: 'character_name',
      width: 150,
      render: (name: string, record: Character) => (
        <Space>
          {record.avatar_url ? (
            <Avatar src={record.avatar_url} size={40} />
          ) : (
            <Avatar icon={<UserOutlined />} size={40} />
          )}
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: '角色类型',
      dataIndex: 'character_type',
      key: 'character_type',
      width: 100,
      render: (type: string) => (
        <Tag color={type === 'main' ? 'blue' : 'default'}>
          {CHARACTER_TYPE_LABELS[type] || type || '未设置'}
        </Tag>
      ),
    },
    {
      title: '扮演者',
      dataIndex: 'played_by',
      key: 'played_by',
      width: 80,
      render: (playedBy?: string) => {
        if (!playedBy) return <Text type="secondary">未设置</Text>
        const label = playedBy === 'player' ? '玩家' : playedBy === 'actor' ? '演员' : playedBy
        return <Tag color={playedBy === 'player' ? 'cyan' : 'orange'}>{label}</Tag>
      },
    },
    {
      title: '角色简介',
      dataIndex: 'intro',
      key: 'intro',
      ellipsis: true,
      render: (intro: string) => (
        <Tooltip title={intro || '暂无简介'}>
          <Text type="secondary" style={{ maxWidth: 300, display: 'block' }}>
            {intro || '暂无简介'}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      render: (_: any, record: Character) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openDetailDrawer(record)}
          >
            详情
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此角色？"
            onConfirm={() => handleDeleteCharacter(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 16 }}>
        <TeamOutlined /> 角色管理
      </Title>

      {/* 剧本选择器 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col>
            <Space>
              <BookOutlined />
              <Text>选择剧本：</Text>
              <Select
                placeholder="请选择剧本"
                value={selectedScriptId}
                onChange={handleScriptChange}
                style={{ width: 300 }}
                allowClear
              >
                {scripts.map((script) => (
                  <Option key={script.id} value={script.id}>
                    <div className="flex items-center justify-between">
                      [{script.id}] {script.title}{' '}
                      <Tag color={script.status === 'published' ? 'green' : 'default'}>
                        {SCRIPT_STATUS_LABELS[script.status] || script.status}
                      </Tag>
                    </div>
                  </Option>
                ))}
              </Select>
              {selectedScriptStatus && (
                <Tag color={selectedScriptStatus === 'published' ? 'green' : 'default'}>
                  当前：{SCRIPT_STATUS_LABELS[selectedScriptStatus] || selectedScriptStatus}
                </Tag>
              )}
            </Space>
          </Col>
          <Col>
            <Search
              placeholder="搜索角色名称"
              onSearch={handleSearch}
              style={{ width: 200 }}
              allowClear
            />
          </Col>
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openEditModal()}
                disabled={!selectedScriptId}
              >
                新建角色
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchCharacters}
                disabled={!selectedScriptId}
              >
                刷新
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 角色列表 */}
      <Card>
        {selectedScriptId ? (
          <Table
            columns={columns}
            dataSource={characters}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, pageSize) => {
                setPagination(prev => ({ ...prev, current: page, pageSize }))
              },
            }}
            scroll={{ x: 1000 }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="请先选择剧本"
          />
        )}
      </Card>

      {/* 新建/编辑角色弹窗 */}
      {selectedScriptId && (
        <CharacterFormModal
          visible={modalVisible}
          onCancel={() => {
            setModalVisible(false)
            setEditingCharacter(null)
          }}
          onOk={handleSaveCharacter}
          editingCharacter={editingCharacter}
          scriptId={selectedScriptId}
        />
      )}

      {/* 角色详情弹窗 */}
      <CharacterDetailDrawer
        visible={detailVisible}
        character={selectedCharacter}
        onClose={() => {
          setDetailVisible(false)
          setSelectedCharacter(null)
        }}
      />
    </div>
  )
}

export default CharacterManagement
